import { useState, useEffect } from 'react';
import { setStaffToken } from '../lib/staffSession';

const API = process.env.REACT_APP_API_URL;

// Staff roles whose home screen is the receipt-capture app rather than the
// admin portal. Mirrors the capture guard in backend/lambda/index.js.
const CAPTURE_HOME_ROLES = ['receipt_capturer', 'technician', 'delivery'];

export default function SetupPassword({ onLogin }) {
  const [token, setToken] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [inviteType, setInviteType] = useState('invite'); // 'invite' or 'user_invite'
  const [inviteRole, setInviteRole] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(true);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (!t) { setInvalid(true); setVerifying(false); return; }
    setToken(t);

    // Verify the invite token
    fetch(`${API}/auth/verify-invite?token=${encodeURIComponent(t)}`)
      .then(r => r.json())
      .then(data => {
        if (data.valid) {
          setCustomerName(data.customerName);
          setInviteType(data.type || 'invite');
          setInviteRole(data.role || null);
        } else {
          setInvalid(true);
        }
      })
      .catch(() => setInvalid(true))
      .finally(() => setVerifying(false));
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (password.length < 8) return setError('Password must be at least 8 characters');
    if (password !== confirm) return setError('Passwords do not match');

    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/setup-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, ...(displayName ? { name: displayName } : {}) }),
      });
      const data = await res.json();
      if (res.ok && data.token) {
        if (CAPTURE_HOME_ROLES.includes(data.admin?.role)) {
          // Field staff live in the receipt-capture app, not the admin portal.
          // Routing them to /#admin left them in a view-only portal with no
          // path to the capture surface, so a new technician would finish
          // setup and still be stuck. receipt_capturer never even reached
          // this branch — isAdmin is false for it, so they landed on the
          // customer portal instead.
          setStaffToken('receipts', data.token);
          // technician/delivery also have (scoped) portal access; seed that
          // token too so #admin doesn't ask them to sign in a second time.
          if (data.isAdmin) setStaffToken('manage', data.token);
          window.location.href = '/#receipt-capture';
        } else if (data.isAdmin) {
          // Admin setup complete — stash the admin JWT and route to the admin page.
          setStaffToken('manage', data.token);
          window.location.href = '/#admin';
        } else {
          localStorage.setItem('ss_token', data.token);
          if (onLogin) onLogin(data.token, data.customer);
          else window.location.href = '/';
        }
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch (e) {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }

  if (verifying) return (
    <div style={styles.page}>
      <div style={styles.box}>
        <div style={styles.loader} />
        <p style={styles.sub}>Verifying your invite link...</p>
      </div>
    </div>
  );

  if (invalid) return (
    <div style={styles.page}>
      <div style={styles.box}>
        <div style={styles.icon}>⚠️</div>
        <h2 style={styles.title}>Invalid or Expired Link</h2>
        <p style={styles.sub}>This invite link is no longer valid. Please contact Silver Service to request a new one.</p>
        <a href="mailto:orders@silverservice.com" style={styles.link}>orders@silverservice.com</a>
      </div>
    </div>
  );

  return (
    <div style={styles.page}>
      <div style={styles.box}>
        <div style={styles.logoArea}>
          <img src="/logo.png" alt="Silver Service" style={styles.logoImg} />
          <div style={styles.logoSub}>Online Ordering Portal</div>
        </div>

        <h2 style={styles.title}>Welcome{customerName ? `, ${customerName}` : ''}!</h2>
        <p style={styles.sub}>
          {inviteType === 'admin_invite'
            ? 'Set up your Silver Service staff account.'
            : inviteType === 'user_invite'
            ? `Set up your ${inviteRole === 'customer_admin' ? 'administrator' : 'employee'} account.`
            : 'Create a password to activate your account.'}
        </p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input
              style={styles.input}
              type="password"
              placeholder="At least 8 characters"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Confirm Password</label>
            <input
              style={styles.input}
              type="password"
              placeholder="Re-enter your password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              required
            />
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <button type="submit" style={styles.btn} disabled={loading}>
            {loading ? 'Setting up...' : 'Activate My Account →'}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  box: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '16px', padding: '40px', maxWidth: '420px', width: '100%', display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' },
  logoArea: { marginBottom: '8px' },
  logoImg: { height: '50px', width: 'auto' },
  logoSub: { fontSize: '12px', color: 'rgba(0,0,0,0.4)', marginTop: '8px' },
  icon: { fontSize: '40px' },
  title: { color: '#333', fontSize: '22px', fontWeight: '700', margin: 0 },
  sub: { color: 'rgba(0,0,0,0.5)', fontSize: '14px', margin: 0, lineHeight: 1.6 },
  link: { color: '#A50B18', fontSize: '14px' },
  form: { width: '100%', display: 'flex', flexDirection: 'column', gap: '16px', textAlign: 'left' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '11px', color: '#A50B18', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 },
  input: { background: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '12px 14px', color: '#333', fontSize: '14px', outline: 'none' },
  error: { background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.25)', borderRadius: '8px', padding: '12px', color: '#c0392b', fontSize: '13px' },
  btn: { background: '#A50B18', color: '#fff', border: 'none', borderRadius: '8px', padding: '14px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', width: '100%' },
  loader: { width: '32px', height: '32px', border: '2px solid rgba(165,11,24,0.2)', borderTopColor: '#A50B18', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
};
